import {inject, Injectable} from "@angular/core";
import {DataStorageService} from "src/app/infrastructure/data-storage.service";
import {TokenWrapper} from "src/app/domain/domain-models";
import {Router} from "@angular/router";
import {BootstrapService} from "src/app/infrastructure/init/bootstrap.service";
import {FarmReference} from "src/app/infrastructure/moovement-api/models/farm";
import {onResume} from "src/app/framework";
import {FathomService} from "src/app/analytics/fathom.service";
import {anonymize} from "src/app/analytics/analytics";
import {environment} from "src/app/environments/environment";
import {PushNotifications} from "@capacitor/push-notifications";
import {RefreshService} from "src/app/infrastructure/init/refresh/refresh.service";
import {UserService} from "src/app/domain/user/user.service";
import {UserStore} from "src/app/domain/user/user.store";

@Injectable({
  providedIn: 'root'
})
export class InitService {

  private router = inject(Router);
  private userStore = inject(UserStore);

  constructor(
    private bootstrapService: BootstrapService,
    private dataStorageService: DataStorageService,
    private fathomService: FathomService,
    private refreshService: RefreshService,
    private userService: UserService
  ) {
    onResume(() => this.resumeApp());
  }

  private async refresh() {
    const shouldRefresh = await this.refreshService.shouldRefresh();
    if (!shouldRefresh) return

    this.bootstrapService.refresh();
  }

  async openDatabaseConnection() {
    await this.dataStorageService.openDatabaseConnection();
  }

  async setUpApp() {
    const authToken = await this.dataStorageService.getValueByKey<TokenWrapper>('authToken');
    if (!authToken?.token) {
      this.router.navigate(['/login']);
      return;
    }

    await this.userService.refresh();
    this.trackSetUp();
    await this.initialiseData();
    await this.refresh();
  }

  private async trackSetUp() {
    const userId = this.userStore.user()?.id;
    if (!userId) return;

    const anonimizedId = await anonymize(userId);
    this.fathomService.trackEvent('App set up for "' + anonimizedId + '"');
  }

  private async resumeApp() {
    await this.dataStorageService.openDatabaseConnection();

    const authToken = await this.dataStorageService.getValueByKey<TokenWrapper>('authToken');
    if (!authToken?.token) {
      this.router.navigate(['/login']);
      return;
    }

    await this.userService.refresh();
    this.refreshNotificationToken();
    this.refresh();
  }

  private refreshNotificationToken() {
    if (environment.platform === 'web') return;

    PushNotifications.register();
  }

  private async initialiseData() {
    const lastSelectedFarmReference = await this.dataStorageService.getValueByKey<FarmReference>('lastSelectedFarmReference');
    if (lastSelectedFarmReference) {
      this.bootstrapService.init(lastSelectedFarmReference);
      return;
    }

    this.bootstrapService.init();
  }
}
